import { useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { addonsApi, deployKeysApi, domainsApi, instancesApi } from '../api/client'
import type { GithubAuthMode, UserDeployKey } from '../api/client'

export function useAddons(instanceId: string) {
  return useQuery({
    queryKey: ['addons', instanceId],
    queryFn: () => addonsApi.list(instanceId),
    enabled: !!instanceId,
    refetchInterval: (query) => {
      const addons = query.state.data
      if (!addons) return false
      const hasActive = addons.some(
        (a) => a.status === 'installing' || a.status === 'updating' || a.status === 'queued' || a.status === 'pending_review'
      )
      return hasActive ? 2000 : false
    },
  })
}

export function useUploadAddon(instanceId: string) {
  const qc = useQueryClient()
  const [progress, setProgress] = useState(0)
  const abortRef = useRef<AbortController | null>(null)
  const mutation = useMutation({
    mutationFn: (file: File) => {
      setProgress(0)
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      return addonsApi.upload(instanceId, file, setProgress, ctrl.signal)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addons', instanceId] }),
  })
  const abort = () => abortRef.current?.abort()
  return Object.assign(mutation, { progress, abort })
}

export function useInstallFromGitHub(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ githubUrl, ref, auth, modules }: { githubUrl: string; ref: string; auth: GithubAuthMode; modules?: string[] }) =>
      addonsApi.installFromGitHub(instanceId, githubUrl, ref, auth, modules),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['addons', instanceId] })
    },
  })
}

// useGithubBranches is the cheap URL-entry probe (no archive download). Fires when
// the debounced URL is a valid GitHub repo so the branch input can become a select.
export function useGithubBranches(
  instanceId: string,
  githubUrl: string,
  auth: GithubAuthMode,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['github-branches', instanceId, githubUrl, auth],
    queryFn: () => addonsApi.githubBranches(instanceId, githubUrl, auth),
    enabled: enabled && !!githubUrl && /^https?:\/\/github\.com\/[^/]+\/[^/]+/i.test(githubUrl),
    staleTime: 30 * 1000,
    retry: false,
  })
}

// usePreviewModules downloads the repo archive and lists its modules — fired on
// the "Install" click so the checkbox picker can open. A mutation (not a query)
// because it's an explicit, potentially slow action.
export function usePreviewModules(instanceId: string) {
  return useMutation({
    mutationFn: ({ githubUrl, ref, auth }: { githubUrl: string; ref: string; auth: GithubAuthMode }) =>
      addonsApi.previewGitHub(instanceId, githubUrl, ref, auth),
  })
}

// Legacy: instance-scoped deploy key. Still wired up for backwards-compat but
// no longer used by the addon install flow — see useEnsureUserDeployKey.
export function useDeployKey(instanceId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['deploy-key', instanceId],
    queryFn: () => instancesApi.deployKey(instanceId),
    enabled: enabled && !!instanceId,
    staleTime: Infinity,
  })
}

// Per-(current user, GitHub repo) deploy key. Generated on first request and
// reused for every future install/sync of the same repo on any of the user's
// instances. Safe to call whenever a repo_url becomes valid — idempotent.
export function useEnsureUserDeployKey(repoUrl: string, enabled: boolean) {
  return useQuery({
    queryKey: ['user-deploy-key', repoUrl],
    queryFn: () => deployKeysApi.ensure(repoUrl),
    enabled: enabled && !!repoUrl && /^https?:\/\/github\.com\/[^/]+\/[^/]+/i.test(repoUrl),
    staleTime: Infinity,
    retry: false,
  })
}

export function useUserDeployKeys(enabled = true) {
  return useQuery<UserDeployKey[]>({
    queryKey: ['user-deploy-keys'],
    queryFn: deployKeysApi.list,
    enabled,
    staleTime: 60 * 1000,
  })
}

export function useDeleteUserDeployKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deployKeysApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-deploy-keys'] }),
  })
}

export function useInstallAddon(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (addonId: string) => addonsApi.install(instanceId, addonId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addons', instanceId] }),
  })
}

export function useUpdateAddon(instanceId: string) {
  const qc = useQueryClient()
  const [progress, setProgress] = useState(0)
  const abortRef = useRef<AbortController | null>(null)
  const mutation = useMutation({
    mutationFn: ({ addonId, file, githubUrl, ref, auth, moduleName }: { addonId: string; file?: File; githubUrl?: string; ref?: string; auth?: GithubAuthMode; moduleName?: string }) => {
      setProgress(0)
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      return addonsApi.update(instanceId, addonId, file, githubUrl, ref, auth ?? 'public', setProgress, ctrl.signal, moduleName)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addons', instanceId] }),
  })
  const abort = () => abortRef.current?.abort()
  return Object.assign(mutation, { progress, abort })
}

export function useAddonJobs(instanceId: string, addonId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['addon-jobs', instanceId, addonId],
    queryFn: () => addonsApi.listJobs(instanceId, addonId),
    enabled: enabled && !!instanceId && !!addonId,
    refetchInterval: (query) => {
      const jobs = query.state.data
      if (!jobs || jobs.length === 0) return false
      const latest = jobs[0]
      return latest.status === 'running' ? 2000 : false
    },
  })
}

export function useApproveAddon(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (addonId: string) => addonsApi.approve(instanceId, addonId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addons', instanceId] }),
  })
}

export function useRejectAddon(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (addonId: string) => addonsApi.reject(instanceId, addonId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addons', instanceId] }),
  })
}

export function useDeleteAddon(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (addonId: string) => addonsApi.delete(instanceId, addonId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addons', instanceId] }),
  })
}

export function useSyncAddon(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (addonId: string) => addonsApi.sync(instanceId, addonId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addons', instanceId] }),
  })
}

export function useUpdateInstanceSettings(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (settings: { auto_db_restore_on_failure?: boolean }) =>
      instancesApi.updateSettings(instanceId, settings),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances', instanceId] }),
  })
}

export function useBindDomain(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (domain: string) => domainsApi.bind(instanceId, domain),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances', instanceId] }),
  })
}

export function useUnbindDomain(instanceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (domain: string) => domainsApi.unbind(instanceId, domain),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances', instanceId] }),
  })
}
